import LoadingCircle from "reports/components/simple/LoadingCircle";
import EOSReport from "../../../../components/Reports/EOSReport";
import { isEmpty } from "lodash";
import MetricAndPnLBreakdowns from "../../../../components/Reports/GenericCharts/MetricAndPnLBreakdowns/MetricAndPnLBreakdowns";
import {
  useIs24Hours,
  useIsShiftly,
  useIsWeekly,
  useProcessGroupNameAndMachemetryIds,
} from "../../../../components/Reports/reportUtils";
import PnLChart from "../../../../components/Reports/GenericCharts/PnLChart/PnLChart";
import {
  getPnlTimes,
  getPnlTimesWithProducingLosses,
} from "../../../../components/Reports/GenericCharts/PnLChart/pnlChartUtils";
import useEosData from "reports/store/useEosData";
import { MetricEnum } from "reports/utils/metricTypes";
import GenericSummaryMetrics from "./GenericSummaryMetrics";
import { ShiftPnlBreakdowns } from "reports/components/Reports/GenericCharts/MetricAndPnLBreakdowns/ShiftPnlBreakdowns";
import { useReportConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

import Pareto from "reports/components/Reports/BespokeReports/Pareto";
import { ONE_MINUTE_OF_MS } from "reports/utils/timeUtils";
import { handleCustomTitle } from "../eosUtils";
import { useMount } from "reports/constants/constants";
import { useAtom } from "jotai";
import { errorOverlayStateAtom } from "reports/components/ErrorOverlay";
import { useIsAdmin } from "utils/customHooks/genericHooks";
import { Link, useParams } from "react-router-dom";
import { MetricsTrendChart } from "reports/components/Reports/GenericCharts/MetricsTrendChart";
import { lastNWeekDataAtom } from "reports/store/store";
import styled from "styled-components/macro";
import { ReportParams } from "reports/types";
import { MIDGARD_FAST_RECOVERY_TIME_BEFORE_SHOW_BANNER } from "contexts/ErrorManager/ErrorManager";
import { ShiftAndProcessPnlBreakdowns } from "reports/components/Reports/GenericCharts/MetricAndPnLBreakdowns/ShiftAndProcessPnlBreakdowns";
import { ReportRangeEnum } from "components/DataGetter/FlightConfig/FlightConfigTypes/ReportPageConfigType";

export default function GenericEOS() {
  const reportConfig = useReportConfig();
  const {
    processGroupName,
    machemetryIds,
    // TODO Change to defaultProcessGroupName to name from https://portal.raven.ai/<TENANT>/processgroups (or create all group if needed)
  } = useProcessGroupNameAndMachemetryIds({
    defaultProcessGroupName: "All",
    machemetryIdsToExclude:
      reportConfig?.eosReportGeneralConfig?.machemetryIdsToExclude ?? [],
  });

  // TODO Change to ge and lt that shows data for TENANT
  // EOS needs ge and lt url params, sample is Jun 26 2023, 14:20 - 22:20
  const sampleLink = `/${processGroupName}?ge=1687803619000&lt=1687832419000`;

  const is24Hour = useIs24Hours();
  const isWeekly = useIsWeekly();
  const shouldShowShiftBreakdown = isWeekly;
  const shouldShowShiftAndProcessBreakdown = is24Hour;
  const eosReportGeneralConfig = reportConfig?.eosReportGeneralConfig;
  const flightTimelineLinkGroupMatchesTimeRange =
    !eosReportGeneralConfig?.flightTimelineLinkGroup?.onlyShowForRanges ||
    eosReportGeneralConfig?.flightTimelineLinkGroup?.onlyShowForRanges.includes(
      isWeekly ? ReportRangeEnum.week : ReportRangeEnum.day
    );
  const flightTimelineLinkGroup = flightTimelineLinkGroupMatchesTimeRange
    ? eosReportGeneralConfig?.flightDashboardLinkGroup?.processGroupName
    : undefined;
  const flightDashboardLinkGroupMatchesTimeRange =
    !eosReportGeneralConfig?.flightDashboardLinkGroup?.onlyShowForRanges ||
    eosReportGeneralConfig?.flightDashboardLinkGroup?.onlyShowForRanges.includes(
      isWeekly ? ReportRangeEnum.week : ReportRangeEnum.day
    );
  const flightDashboardLinkGroup = flightDashboardLinkGroupMatchesTimeRange
    ? eosReportGeneralConfig?.flightDashboardLinkGroup?.processGroupName
    : undefined;
  const showReportLinks = eosReportGeneralConfig?.showReportLinks;
  const shouldShowReportLink =
    typeof showReportLinks === "boolean"
      ? showReportLinks
      : showReportLinks?.includes(
          isWeekly
            ? ReportRangeEnum.week
            : is24Hour
              ? ReportRangeEnum.shift
              : ReportRangeEnum.day
        );
  const trendChartConfig = eosReportGeneralConfig?.trendChart || null;
  const numOfWeeks = reportConfig?.weeklyReportConfig?.numOfWeeks;

  return (
    <>
      {machemetryIds && machemetryIds.length > 0 ? (
        <EOSReport
          numOfWeeks={numOfWeeks}
          machemetryIds={machemetryIds}
          sampleLink={sampleLink}
          shouldShowDownload={!eosReportGeneralConfig?.shouldHideDownload}
          // TODO Change to flightEndpoint to appropriate timeline page from https://flight.raven.ai/<TENANT>/dashboard/<FLIGHT_ENDPOINT>?view=tl
          flightTimelineLinkGroup={
            shouldShowReportLink
              ? (flightTimelineLinkGroup ?? processGroupName)
              : undefined
          }
          // TODO Change to flightGroup to appropriate dashboard page from https://flight.raven.ai/<TENANT>/dashboard/<FLIGHT_GROUP>
          flightDashboardLinkGroup={
            shouldShowReportLink
              ? (flightTimelineLinkGroup ??
                flightDashboardLinkGroup ??
                processGroupName)
              : undefined
          }
          shouldShowPrint={eosReportGeneralConfig?.shouldShowPrint ?? false}
          shouldSkipSkuData={eosReportGeneralConfig?.shouldSkipSkuData ?? false}
          // TODO Remove 45 and related lines in <Tenant>EosComponents if shift breakdown is not needed
          shouldFetchDataByShift={
            shouldShowShiftBreakdown || shouldShowShiftAndProcessBreakdown
          }
          shouldFetchDataByShiftAndProcess={shouldShowShiftAndProcessBreakdown}
          shouldFetchLastNWeeksData={isWeekly && trendChartConfig !== null}
          customTitle={eosReportGeneralConfig?.customHeaderTitle}
          shouldShowTranslate={
            eosReportGeneralConfig?.shouldShowTranslate ?? false
          }
        >
          <GenericEosComponents
            {...{
              processGroupName,
              machemetryIds,
              shouldShowShiftBreakdown,
              shouldShowShiftAndProcessBreakdown,
              trendChartConfig,
            }}
          />
        </EOSReport>
      ) : (
        <>
          <LoadingCircle
            logAfterTimeout={{
              logData: { machemetryIds },
              delay: 5 * 1000,
            }}
          />
          <SuggestPotentialFixes {...{ machemetryIds, delay: 5 * 1000 }} />
        </>
      )}
    </>
  );
}

function GenericEosComponents({
  processGroupName,
  machemetryIds,
  shouldShowShiftBreakdown,
  shouldShowShiftAndProcessBreakdown,
  trendChartConfig,
}) {
  const { data } = useEosData();
  const [lastNWeekData] = useAtom(lastNWeekDataAtom);
  const reportConfig = useReportConfig();
  const summaryMetricsInputList = reportConfig?.eosReportMetricsConfig
    ?.summaryMetricsInputList ?? [
    { name: MetricEnum.AVAIL },
    { name: MetricEnum.TAGGING },
  ];

  // filter metrics that are only shown in specific charts
  const shiftSummaryMetricsInputList = summaryMetricsInputList.find(m =>
    m.onlyShowInCharts?.includes("shiftBreakdown")
  )
    ? summaryMetricsInputList.filter(m =>
        m.onlyShowInCharts?.includes("shiftBreakdown")
      )
    : summaryMetricsInputList.filter(m => !("onlyShowInCharts" in m));

  const pnlBreakdownssummaryMetricsInputList = summaryMetricsInputList.find(m =>
    m.onlyShowInCharts?.includes("pnlBreakdown")
  )
    ? summaryMetricsInputList.filter(m =>
        m.onlyShowInCharts?.includes("pnlBreakdown")
      )
    : summaryMetricsInputList.filter(m => !("onlyShowInCharts" in m));

  const isShiftly = useIsShiftly();
  const is24Hour = useIs24Hours();
  const isWeekly = useIsWeekly();
  const showReportLinks = reportConfig?.eosReportGeneralConfig?.showReportLinks;
  const shouldShowReportLink =
    typeof showReportLinks === "boolean"
      ? showReportLinks
      : showReportLinks?.includes(
          isWeekly
            ? ReportRangeEnum.week
            : is24Hour
              ? ReportRangeEnum.day
              : ReportRangeEnum.shift
        );
  const processGroupNameDisplay =
    reportConfig?.eosReportGeneralConfig?.customTitlePrefixByGroup?.[
      processGroupName
    ] ?? processGroupName;
  return data && !isEmpty(data) && machemetryIds && machemetryIds.length > 0 ? (
    <>
      <GenericSummaryMetrics
        {...{ data, processGroupName: processGroupNameDisplay }}
      />
      <>
        {reportConfig?.eosReportGeneralConfig?.topDowntimesReports
          ?.filter(({ enabled }) => enabled)
          ?.map(
            (
              {
                reportRanges,
                categoriesToSkip,
                customTitle,
                numberOfDowntimes,
                xLabel,
                shouldShowHideLessThan1Percent,
                showDurationLabel,
                timeDivisorMs,
              },
              idx
            ) => {
              const shouldShowHideLessThan1PercentFromUrl =
                window.location.search.includes("showMore=true")
                  ? false
                  : undefined;
              const topDowntimesProps = {
                height: 500,
                xLabel: xLabel ?? "Downtimes",
                numberOfDowntimes: numberOfDowntimes ?? 5,
                shouldShowHideLessThan1Percent:
                  shouldShowHideLessThan1PercentFromUrl ??
                  shouldShowHideLessThan1Percent ??
                  false,
                timeDivisor: timeDivisorMs ?? ONE_MINUTE_OF_MS,
                showDurationLabel: showDurationLabel ?? false,
              };
              const show =
                (reportRanges.includes(ReportRangeEnum.shift) && isShiftly) ||
                (reportRanges.includes(ReportRangeEnum.day) && is24Hour) ||
                (reportRanges.includes(ReportRangeEnum.week) && isWeekly);

              return show ? (
                <Pareto
                  key={idx}
                  pnlBreakdown={data}
                  categoriesToSkip={categoriesToSkip}
                  title={
                    customTitle
                      ? handleCustomTitle(customTitle, {
                          numberOfDowntimes:
                            topDowntimesProps.numberOfDowntimes,
                        })
                      : `Top ${topDowntimesProps.numberOfDowntimes} Downtimes`
                  }
                  {...topDowntimesProps}
                />
              ) : null;
            }
          )}
      </>
      <PnLChart
        pnlTimes={
          reportConfig?.eosReportGeneralConfig?.shouldUseProducingLosses
            ? getPnlTimesWithProducingLosses({
                data,
                producingLossesToSkip:
                  reportConfig?.eosReportGeneralConfig?.producingLossesToSkip ??
                  [],
              })
            : getPnlTimes({
                data,
              })
        }
        shouldShowAvgDuration={
          reportConfig?.eosReportGeneralConfig?.shouldShowAvgDuration
        }
      />
      <div className="generic-eos-page-break break-before" />
      {isWeekly && trendChartConfig && (
        <MetricsTrendChart
          summaryMetricInputList={trendChartConfig.summaryMetricsInputList}
          pnlBreakdowns={lastNWeekData}
          descendingOrder={trendChartConfig.descendingOrder ?? true}
          width={1500}
        />
      )}
      {shouldShowShiftBreakdown && (
        <ShiftPnlBreakdowns
          summaryMetricsInputList={shiftSummaryMetricsInputList}
        />
      )}
      {shouldShowShiftAndProcessBreakdown && (
        <ShiftAndProcessPnlBreakdowns
          summaryMetricsInputList={summaryMetricsInputList}
        />
      )}
      <MetricAndPnLBreakdowns
        {...{
          machemetryIds,
          summaryMetricsInputList: pnlBreakdownssummaryMetricsInputList,
          showReportLink: shouldShowReportLink,
        }}
      />
      <div className="spacer" style={{ paddingBottom: 96 }}></div>
    </>
  ) : (
    <>
      <LoadingCircle
        logAfterTimeout={{
          logData: { eosData: data, machemetryIds, lastNWeekData },
          delay: 5 * 1000,
        }}
      />
      <SuggestPotentialFixes
        {...{
          machemetryIds,
          delay: MIDGARD_FAST_RECOVERY_TIME_BEFORE_SHOW_BANNER + 1000,
        }}
      />
    </>
  );
}
function SuggestPotentialFixes({ machemetryIds, delay }) {
  const { processGroupName: processGroupNameFromUrl } =
    useParams() as ReportParams;
  const [errorOverlayState, setErrorOverlayState] = useAtom(
    errorOverlayStateAtom
  );
  const isAdminUser = useIsAdmin();
  const { ckey } = useParams() as ReportParams;

  useMount(() => {
    const timer = window.setTimeout(() => {
      if (machemetryIds.length === 0) {
        setErrorOverlayState(prev =>
          // if there's already an error, leave it. Otherwise update it.
          prev.error
            ? prev
            : {
                ...errorOverlayState,
                error: true,
                message: (
                  <NoUsedLinksStyles>
                    <div>
                      Server unresponsive — please{" "}
                      <a href={window.location.href}>try again</a> later. If the
                      problem persists,{" "}
                      <a
                        href="https://raven.ai/contact-us/"
                        target="_blank"
                        rel="noreferrer"
                      >
                        contact support
                      </a>
                      .
                    </div>
                    <br />
                    <div>
                      {"("}No processes found in process group{" "}
                      {processGroupNameFromUrl ?? "PROCESS_GROUP_UNSPECIFIED"}.
                      {")"}
                    </div>
                    {isAdminUser ? (
                      <>
                        <br />
                        <div>
                          Possible misconfiguration — check your process groups
                          at{" "}
                          <Link
                            onClick={() => {
                              setErrorOverlayState({
                                ...errorOverlayState,
                                error: false,
                                message: "",
                              });
                            }}
                            to={`/${ckey}/processgroups`}
                          >{`/${ckey}/processgroups`}</Link>
                        </div>
                      </>
                    ) : null}
                  </NoUsedLinksStyles>
                ),
              }
        );
      }
    }, delay);
    // if this component unmounts, the app finished loading, so problem was temporary => clear the error message
    return () => {
      window.clearTimeout(timer);
      if (errorOverlayState.error) {
        setErrorOverlayState({
          ...errorOverlayState,
          error: false,
          message: "",
        });
      }
    };
  });
  return null;
}
const NoUsedLinksStyles = styled.div`
  a {
    color: #1a73e8;
    text-decoration: none;
  }
`;
